import * as React from "react";
import { OPERATOR_FLOOR_MAX_CENTS } from "@ewn/shared";
import { listFloors, setCoinEnabled, setOperatorFloor, type AdminFloorRow } from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { centsAsInput, centsFrom, money } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Notice, Warnings } from "@/components/ui/feedback";
import { Field, Input } from "@/components/ui/input";
import { ConfirmModal, Modal } from "@/components/ui/modal";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonRows } from "@/components/ui/skeleton";
import { TableShell, TBody, TD, TEmpty, TH, THead, TR } from "@/components/ui/table";

/** The button that changes a coin's state, named for what it will do. */
function verbFor(enabled: boolean): string {
  return enabled ? "Switch off" : "Switch on";
}

/** "USDT Tron (TRC20)", or just "BTC" where the ticker is the network. */
function coinName(row: AdminFloorRow): string {
  return row.ticker === row.network_label ? row.ticker : `${row.ticker} ${row.network_label}`;
}

/**
 * Which coins are offered, and the smallest order each is offered for.
 *
 * A coin switched off is hidden at checkout; invoices already open on it are
 * still watched and settled. Both directions are confirmed; switching one ON
 * warns that a coin the provider does not offer fails when a customer picks
 * it -- that is why XMR is off.
 *
 * A coin whose minimum an order does not reach is hidden from the customer and
 * refused at checkout. The operator's minimum is for a coin whose Plisio
 * commission has a fixed floor that eats a small order; the learned one is what
 * Plisio's refusals and acceptances say. The shop applies the higher. Saved
 * minimums apply at once -- the storefront reads them live.
 */
export function FloorsScreen() {
  const { data, loading, error, reload } = useAsync(() => listFloors(), []);
  const [editing, setEditing] = React.useState<AdminFloorRow | null>(null);
  const [switching, setSwitching] = React.useState<AdminFloorRow | null>(null);
  const toggle = useAction();
  const rows = data?.rows ?? [];
  // The worker refuses switching off the last coin offered; the button says
  // so first, rather than after a confirmation.
  const lastOffered = rows.filter((r) => r.enabled).length === 1;
  const pinned = (row: AdminFloorRow) => row.enabled && lastOffered;
  const verb = switching ? verbFor(switching.enabled) : "";

  async function confirmSwitch() {
    if (!switching) return;
    await toggle.run(() => setCoinEnabled(switching.psys_cid, !switching.enabled));
    // Reloaded whether it worked or not: a refusal is usually a colleague
    // having switched it first, and the list should show where it is now.
    setSwitching(null);
    reload();
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Coins"
        description={
          "Which coins checkout offers, and the smallest order each is offered for. " +
          "The higher of your minimum and the learned one applies."
        }
      />
      {error && <Notice tone="error">{error}</Notice>}
      {toggle.error && <Notice tone="error">{toggle.error}</Notice>}
      {/* A switch stands even when the site does not rebuild; this says the
          FAQ still names the coins it named before, and where to publish. */}
      <Warnings warnings={toggle.warnings} />

      <Card>
        {loading && rows.length === 0 ? (
          <SkeletonRows rows={4} cols={6} />
        ) : (
          <TableShell>
            <THead>
              <TH>Coin</TH>
              <TH>At checkout</TH>
              <TH numeric>Your minimum</TH>
              <TH numeric>Learned</TH>
              <TH numeric>Applied</TH>
              <TH />
            </THead>
            <TBody>
              {rows.length === 0 && <TEmpty colSpan={6}>No coins.</TEmpty>}
              {rows.map((row) => (
                <TR key={row.psys_cid}>
                  <TD>
                    <span className="font-medium">{row.ticker}</span>{" "}
                    <span className="text-xs text-muted-foreground">{row.network_label}</span>
                  </TD>
                  <TD>
                    {row.enabled ? (
                      <Badge color="green">offered</Badge>
                    ) : (
                      <Badge color="gray">off</Badge>
                    )}
                  </TD>
                  <TD numeric>{row.operator_cents === null ? "—" : money(row.operator_cents)}</TD>
                  <TD numeric className="text-muted-foreground">
                    {row.learned_cents === null ? "—" : money(row.learned_cents)}
                  </TD>
                  <TD numeric className="font-medium">
                    {row.effective_cents === null ? "none" : money(row.effective_cents)}
                  </TD>
                  <TD>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditing(row)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={toggle.busy || pinned(row)}
                        title={
                          pinned(row)
                            ? "The last coin offered stays on; turn crypto off in Settings"
                            : undefined
                        }
                        onClick={() => setSwitching(row)}
                      >
                        {verbFor(row.enabled)}
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableShell>
        )}
      </Card>

      <ConfirmModal
        open={switching !== null}
        title={switching ? `${verb} ${coinName(switching)}?` : ""}
        body={
          switching?.enabled
            ? "Customers stop seeing it at checkout. Invoices already open on it are still " +
              "watched and settled."
            : "Customers see it at checkout again. Switch it on only if Plisio offers it " +
              "now: a coin the provider will not open an invoice for fails when a customer " +
              "picks it."
        }
        confirmLabel={verb}
        busy={toggle.busy}
        onConfirm={() => void confirmSwitch()}
        onClose={() => setSwitching(null)}
      />

      <FloorDialog
        row={editing}
        onDone={() => {
          setEditing(null);
          reload();
        }}
        onCancel={() => setEditing(null)}
      />
    </div>
  );
}

function FloorDialog({
  row,
  onDone,
  onCancel,
}: {
  row: AdminFloorRow | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [text, setText] = React.useState("");
  const action = useAction();
  const id = React.useId();
  const reset = action.reset;

  React.useEffect(() => {
    if (!row) return;
    setText(row.operator_cents === null ? "" : centsAsInput(row.operator_cents));
    reset();
  }, [row, reset]);

  if (!row) return null;
  const cents = centsFrom(text);
  const valid = cents !== null && cents > 0 && cents <= OPERATOR_FLOOR_MAX_CENTS;

  async function save(value: number | null) {
    if (!row) return;
    if (await action.run(() => setOperatorFloor(row.psys_cid, value))) onDone();
  }

  return (
    <Modal
      open
      title={`Minimum for ${coinName(row)}`}
      onClose={onCancel}
      footer={
        <>
          {row.operator_cents !== null && (
            <Button variant="outline" disabled={action.busy} onClick={() => void save(null)}>
              Remove minimum
            </Button>
          )}
          <Button disabled={action.busy || !valid} onClick={() => void save(cents)}>
            {action.busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label="Minimum order, USD"
          htmlFor={id}
          hint="Orders below this do not see the coin. Applies at once."
        >
          <Input id={id} inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} placeholder="10.00" />
        </Field>
        {action.error && <Notice tone="error">{action.error}</Notice>}
      </div>
    </Modal>
  );
}
